import { getFrontendTranslations } from "@generated/api/txtRestControllerApi";
import { dictToList } from "@utils/dictUtils";
import type { Language } from "@utils/type/type";
import { delay } from "@utils/asyncUtils";

type TranslationSimpleFn = (key: string, list?: string[]) => string | undefined;
type TranslationWithDefaultMsgFn = (key: string, defaultMsg: string) => string;
type TranslationWithListWithDefaultMsgFn = (key: string, list: string[], defaultMsg: string) => string;
export type TranslationFn = TranslationSimpleFn & TranslationWithDefaultMsgFn & TranslationWithListWithDefaultMsgFn;

type InternalDataState<T> =
  | {
      state: "AVAILABLE";
      value: T;
    }
  | {
      state: "MISSING" | "LOADING";
    };

type InternalData = {
  [Property in Language]: InternalDataState<Map<string, string>>;
};

function initialInternalData(): InternalData {
  return {
    en: {
      state: "MISSING",
    },
    de: {
      state: "MISSING",
    },
    es: {
      state: "MISSING",
    },
    ru: {
      state: "MISSING",
    },
    zh: {
      state: "MISSING",
    },
    pt: {
      state: "MISSING",
    },
    fr: {
      state: "MISSING",
    },
  };
}

const internalData = initialInternalData();

async function loadAsyncData(lang: Language, loader: (lang: Language) => Promise<Map<string, string>>): Promise<Map<string, string>> {
  while (true) {
    const currentStatus = internalData[lang];

    if (currentStatus.state === "AVAILABLE") {
      // Data has already been set up
      return currentStatus.value;
    }

    if (currentStatus.state === "MISSING") {
      // Run loader for the first time
      internalData[lang] = {
        state: "LOADING",
      } satisfies InternalDataState<unknown>;

      const data = await loader(lang);

      internalData[lang] = {
        state: "AVAILABLE",
        value: data,
      } satisfies InternalDataState<unknown>;
      return data;
    }

    // There is a request for the question in process
    await delay(100);
  }
}

function interpolateString(text: string, list: string[]): string {
  return list.reduce((acc, curr, index) => {
    return acc.replace(new RegExp(`\\{${index}\\}`, "g"), curr);
  }, text);
}

async function getGaDesignTranslations(language: Language): Promise<Map<string, string>> {
  return await loadAsyncData(language, async (lang: Language) => {
    const response = await getFrontendTranslations("DESIGN", lang);
    return new Map(dictToList(response.keys.additionalProperties));
  });
}

export async function getTranslationFunction(lang: Language): Promise<TranslationFn> {
  const translations = await getGaDesignTranslations(lang);
  const translationFn: TranslationSimpleFn = (key, list) => {
    const translatedText = translations.get(key);
    if (translatedText === undefined || list === undefined) {
      return translatedText;
    }
    return interpolateString(translatedText, list);
  };

  const translationWithDefaultMsgFn: TranslationWithDefaultMsgFn = (key, defaultMsg) => {
    const translatedText = translations.get(key);
    return translatedText ?? defaultMsg;
  };

  const translationWithListWithDefaultMsgFn: TranslationWithListWithDefaultMsgFn = (key, list, defaultMsg) => {
    const translatedText = translations.get(key);
    if (translatedText === undefined) {
      return defaultMsg;
    }
    return interpolateString(translatedText, list);
  };

  return ((key: string, ...args: [] | [string[]] | [string] | [string[], string]) => {
    if (args.length === 0) {
      return translationFn(key);
    } else if (args.length === 1 && typeof args[0] === "string") {
      return translationWithDefaultMsgFn(key, args[0]);
    } else if (args.length === 1) {
      return translationFn(key, args[0] as string[]);
    } else {
      return translationWithListWithDefaultMsgFn(key, args[0], args[1]);
    }
  }) as TranslationFn;
}
